import { z } from 'zod';

import type { PK } from './database.schema';
import { baseDatabaseSchema, userSchema } from './database.schema';

export const attachmentSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    fileName: z.string(),
    size: z.number(),
    path: z.string(),
    type: z.enum(['pre', 'post']).optional(),
    status: z.enum(['pending', 'ready']).optional(),
    malware_scan_status: z
      .enum([
        // Official AWS statuses
        // https://docs.aws.amazon.com/guardduty/latest/ug/monitoring-malware-protection-s3-scans-gdu.html
        'NO_THREATS_FOUND',
        'THREATS_FOUND',
        'UNSUPPORTED',
        'ACCESS_DENIED',
        'FAILED',
        // Artificial specific statuses
        'PENDING',
      ])
      .optional(),
    attachment_id: z.string().uuid().optional(),
    attachment_resource_id: z.string().uuid().optional(),
    attachment_resource_name: z.enum(['submissions', 'templates']).or(z.string().startsWith('submissions#')).optional(),
    attachment_created_by: userSchema.optional(),
    block_id: z.string().uuid().optional(),
    block_type: z.enum(['block', 'summary_block']).optional(),
  })
  .strict();

export const attachmentDatabaseSchema = attachmentSchema.merge(baseDatabaseSchema);

// Only clean or not-yet-scanned attachments may be copied to another resource.
// `undefined` covers pre-scan/legacy records; anything else (PENDING, THREATS_FOUND, ...) is withheld.
export const isAttachmentSafeToClone = (attachment: Pick<AttachmentDb, 'malware_scan_status'>): boolean =>
  attachment?.malware_scan_status === 'NO_THREATS_FOUND' || attachment?.malware_scan_status === undefined;

export type Attachment = z.infer<typeof attachmentSchema>;
export type AttachmentDb = z.infer<typeof attachmentDatabaseSchema> & PK;
